import React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import produce from 'immer';
import clsx from 'clsx';

import { getTimeline, getCID } from 'api/actions/contracts'
import { fetcher } from 'api/client';
import { parseDate } from 'utils';
import { timelineEvents } from './eventsMapper';

export default function Timeline({ contract = {}, contractQueryId }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const history = useHistory();
    const { data, isSuccess, isLoading } = useQuery([...contractQueryId, 'timeline'], ({ queryKey: [, contractId] }) => fetcher(getTimeline(contractId)), {
        enabled: !!contract._id,
    })
    const goToIPFS = (cid) => history.push(`/blockchain/${cid}`)
    const compareIPFS = async (cid) => {
        const data = await fetcher(getCID({ cid, contractId: contract._id }));
        queryClient.setQueryData([...contractQueryId, 'timeline'], produce(draft => {
            draft.forEach((v) => v.verified = data.success)
        }));
    }
    return (
        <div className="content-tabs p-2 h-100" style={{ overflow: 'auto' }} >
            <div className="detail border-bottom mt-2 mb-2">
                <span>{t('contracts.timeline.description')}</span>
                <div className="p-1 text-center d-flex justify-content-center">
                    <TimelineLineLabel className="secondary mr-2">{t('contracts.timeline.internal')}</TimelineLineLabel>
                    <TimelineLineLabel className="primary ">{t('contracts.timeline.external')}</TimelineLineLabel>
                </div>
            </div>
            <ul className="timeline">
                {
                    isLoading &&
                    <li>
                        {t('loading.default')}...
                    </li>
                }
                {
                    isSuccess &&
                    data?.map((timeline, index) => {
                        const { type, message, eventDate, properties = {}, to, who, user_to, user_who, group_to, isPostSend, version, _id } = timeline;

                        return (
                            <li key={`${_id}${index}`} className={`${isPostSend ? 'primary' : 'secondary'}`}>
                                <small>{parseDate(eventDate, 'PPp')}</small>
                                {message ?
                                    message :
                                    <div>
                                        <p className="m-0">
                                            <i className={`fas fa-${timelineEvents[type]?.icon}  text-${timelineEvents[type]?.color} mr-2`} /> {' '} <b>{who}</b> {t(`events.${type}`)} <b>{to}</b>
                                        </p>
                                        <b>{' '}{properties?.document}</b>
                                        {type === 'extra' && <b>{' '}{properties?.filename}</b>}
                                        {type === 'set-expire' &&
                                            <p className="m-0">
                                                {' '}<b>{properties.attachment}</b> {t('contracts.timeline.expires')} <b>{parseDate(properties.date, 'PPp')}</b>
                                            </p>
                                        }
                                    </div>
                                }

                                {/* {['collab-message', 'add-collaborator'].includes(type) &&
                                    <b>{user_to?.givenName} {user_to?.familyName}</b>
                                } */}
                                {['zoom-meeting-started', 'zoom-meeting-ended'].includes(type) && !!properties?.meetingId &&
                                    <b>Id de la Reunion: {properties?.meetingId}</b>
                                }
                                {['document-replace', 'create-template', 'create-questionnaire'].includes(type) &&
                                    <b>{properties.filename}</b>
                                }
                                {['vobo-approved', 'vobo-rejected', 'grab-vobo'].includes(type) && group_to &&
                                    <p className="m-0">{t('events.vobo-representing')} {group_to.name}</p>
                                }

                                {type === 'reset-status' &&
                                    <p className="m-0 spaced">
                                        <i className="fas fa-times animated infinite text-danger" /> {t('events.vobos-deleted')}
                                    </p>
                                }

                                <div className="text-small">
                                    {['validate-attachment', 'attachment', 'reject-attachment'].includes(type) &&
                                        <p className="m-0">- {properties.attachment}</p>
                                    }
                                    {['add-voters', 'comment', 'send', 'send-sign', 'send-fill', 'send-negotiation', 'send-accept', 'send-sign-order', 'send-fill-order', 'send-download'].includes(type) &&
                                        <>
                                            {properties.participants?.map((participant, index) => (
                                                <div key={`${participant._id}` + index}> - {participant}</div>
                                            ))}
                                            {!properties.participants?.length && type === 'add-voters' &&
                                                <div> - {t('contracts.timeline.no_voters')}</div>
                                            }
                                            {!properties.participants?.length && type === 'comment' &&
                                                <div> - {t('contracts.timeline.owner')} </div>
                                            }
                                        </>
                                    }
                                    {['comment', 'comment-delete', 'collab-message', 'delegate'].includes(type) && properties.comment &&
                                        <div>
                                            {t('contracts.timeline.message')}<b> "{properties.comment}"</b>
                                        </div>
                                    }
                                    {['vobo-rejected', 'reject', 'document-canceled'].includes(type) && properties.comment &&
                                        <p className="m-0">{t('contracts.timeline.reason')}: <b>"{properties.comment}"</b></p>
                                    }
                                    {timeline.hash &&
                                        <>
                                            <br />
                                            <strong>{t('contracts.timeline.registered_hash')}:</strong>
                                            <p className="m-0">{timeline.hash}</p>
                                        </>
                                    }
                                    {['milestone-attachment-add', 'milestone-attachment-delete'].includes(type) &&
                                        <div>
                                            <small style={{ marginBottom: 4 }}>
                                                {properties.url ?
                                                    <p className="m-0">
                                                        {t('events.filename')} :
                                                            <a href={properties.url} className="inline">
                                                            <i className="fas fa-eye"></i> {properties.filename}
                                                        </a>
                                                    </p>
                                                    :
                                                    <p className="m-0">
                                                        {t('events.filename')}: {properties.filename}
                                                    </p>
                                                }
                                            </small>
                                        </div>
                                    }

                                    {['negotiation', 'version'].includes(type) &&
                                        <small>
                                            <a href={properties.url} className="inline" target="_blank" rel="noreferrer">
                                                <i className="fas fa-eye"></i> {t('contracts.timeline.visualize')}
                                            </a>
                                        </small>
                                    }

                                    {['document-replace', 'collab-message', 'validated-identity'].includes(type) && properties.url &&
                                        <>
                                            <a href={properties.url} className="inline" target="_blank" rel="noreferrer">
                                                <i className="fas fa-eye"></i> {properties.filename}
                                            </a>
                                            <br />
                                        </>
                                    }
                                    {type === 'external-link-send' &&
                                        <a href={properties.url} className="inline" target="_blank" rel="noreferrer">
                                            <i className="fas fa-eye"></i> {t('contracts.timeline.external_link')}
                                        </a>
                                    }
                                    {type === 'reject-attachment' &&
                                        <>
                                            <br />
                                            <p className="m-0">
                                                <i className="fas fa-paper-plane"></i> {t('contracts.timeline.message')}:
                                                    <b>"{properties.comment}"</b>
                                            </p>
                                        </>
                                    }
                                    {properties?.reason &&
                                        <>
                                            <br />
                                            <p className="m-0">
                                                {t('contracts.timeline.message')}:
                                                    <b>"{properties.reason}"</b>
                                            </p>
                                        </>
                                    }
                                    {
                                        timeline.blockchainTx &&
                                        <p className="m-0">
                                            <strong>Blockchain Tx:</strong>
                                            <a className="blockchain" href={`/blockchain/${timeline.blockchainTx}`}>{timeline.blockchainTx}</a>
                                        </p>
                                    }
                                    {/* {timeline.version &&
                                            <div>
                                                <strong>Version:</strong> <p className="m-0">{timeline.version}</p>
                                            </div>
                                        } */}
                                    {timeline.ipld &&
                                        <div onClick={() => goToIPFS(timeline.ipld)} className="pointer">
                                            <strong>IPLD:</strong> <a className="blockchain" target="_blank">{timeline.ipld}</a>
                                        </div>
                                    }
                                    {timeline.cid &&
                                        <>
                                            <div onClick={() => goToIPFS(timeline.cid)} className="pointer">
                                                <strong>IPFS Hash:</strong> <a className="blockchain" target="blank">{timeline.cid}</a>
                                            </div>
                                            <div className={clsx(data.reduce((a, b) => (a?.eventDate > b?.eventDate && a?.type === 'contract-ipfs' ? a : b))?._id === _id || 'd-none')}>
                                                <button className="btn btn-sm btn-primary" onClick={() => compareIPFS(timeline.cid)}>{t('contracts.timeline.verify_last_version')}{' '}
                                                </button>{' '}
                                                <i className={clsx(timeline.verified && 'fas fa-check text-success')}></i>
                                                <i className={clsx(timeline.verified === false && 'fas fa-times text-danger')}></i>
                                            </div>
                                        </>
                                    }
                                    {timeline?.blockchainHash &&
                                        <div>
                                            <p className="m-0"><b>{t('contracts.timeline.Address')}: <a className="text-wrap" href={`${process.env.REACT_APP_ETHERSCAN_URL}/address/${timeline.blockchainHash}`} target="_blank">{timeline.blockchainHash}</a></b></p>
                                        </div>
                                    }
                                    {properties?.blockchainLog &&
                                        <div>
                                            <p className="m-0"><b>{t('contracts.timeline.Hash')}: <a className="text-wrap" href={`${process.env.REACT_APP_ETHERSCAN_URL}/tx/${properties.blockchainLog.hash}`} target="_blank">{properties.blockchainLog.hash}</a></b></p>

                                            {properties.blockchainLog.from &&
                                                <p className="m-0"><b>{t('contracts.timeline.Address_from')}: <a className="text-wrap" href={`${process.env.REACT_APP_ETHERSCAN_URL}/address/${properties.blockchainLog.from}`} target="_blank">{properties.blockchainLog.from}</a></b></p>
                                            }
                                            {properties.blockchainLog.to &&
                                                <p className="m-0"><b>{t('contracts.timeline.Address_to')}: <a className="text-wrap" href={`${process.env.REACT_APP_ETHERSCAN_URL}/address/${properties.blockchainLog.to}`} target="_blank">{properties.blockchainLog.to}</a></b></p>
                                            }
                                            {!timeline.confirmed && timeline.type.includes('-init') && <p className="m-0"> <i className="fa fa-spinner fa-spin"></i> {t('contracts.timeline.executing')}</p>}
                                            {/* {properties?.blockchainLog?.gasUsed && <p className="m-0"><b>{t('contracts.timeline.Gas')}: {properties.blockchainLog.gasUsed}</b></p>}
                                                {properties?.blockchainLog?.amount && <p className="m-0"><b>{t('contracts.timeline.Amount')}: {properties.blockchainLog.amount} ETH</b></p>}
                                                {properties?.blockchainLog?.fee && <p className="m-0"><b>{t('contracts.timeline.Fee')}: {properties.blockchainLog.fee} ETH</b></p>} */}
                                        </div>
                                    }
                                </div>
                            </li>
                        )
                    })
                }
            </ul>
        </div >
    )
};


function TimelineLineLabel({ className, color, children }) {
    return (
        <ul className={'timeline m-0'}>
            <li className={className}> {children}</li>
        </ul>
    )
}
